import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { api, formatAud } from "../lib/api";
import { X, Send, Paperclip, FileText, ExternalLink } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useCart } from "../context/CartContext";

const STORAGE_KEY    = "bcp_chat_v1";
const MAX_FILE_BYTES = 4 * 1024 * 1024;

/* ─── Inject keyframes ────────────────────────────────────────────── */
const KF = `
@keyframes wng_float  {0%,100%{transform:translateY(0)}  50%{transform:translateY(-7px)}}
@keyframes wng_sway   {0%,100%{transform:translate(0,0) rotate(0deg)} 30%{transform:translate(-5px,0) rotate(-1.5deg)} 70%{transform:translate(5px,0) rotate(1.5deg)}}
@keyframes wng_bounce {0%,100%{transform:translateY(0)} 22%{transform:translateY(-24px)} 48%{transform:translateY(-10px)} 72%{transform:translateY(-20px)} 90%{transform:translateY(-4px)}}
@keyframes wng_wiggle {0%,100%{transform:rotate(0) translateY(0)} 20%{transform:rotate(-7deg) translateY(-8px)} 50%{transform:rotate(7deg) translateY(-14px)} 75%{transform:rotate(-4deg) translateY(-6px)}}
@keyframes wng_sleep  {0%,100%{transform:rotate(0) translateY(0)} 50%{transform:rotate(9deg) translateY(4px)}}
@keyframes wng_pulse  {0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.45;transform:scale(0.78)}}
@keyframes wng_antpls {0%,100%{opacity:1} 50%{opacity:0.5}}
@keyframes wng_pop    {0%{transform:scale(0.1) translateY(18px);opacity:0} 62%{transform:scale(1.06) translateY(-4px)} 100%{transform:scale(1) translateY(0);opacity:1}}
@keyframes wng_zzz    {0%{opacity:0;transform:translate(0,0) scale(0.4)} 35%{opacity:1} 100%{opacity:0;transform:translate(12px,-26px) scale(1.1)}}
@keyframes wng_conf   {0%{transform:translateY(0) rotate(0deg);opacity:1} 70%{opacity:0.9} 100%{transform:translateY(-190px) rotate(680deg);opacity:0}}
@keyframes wng_shadow {0%,100%{transform:scaleX(1);opacity:0.18} 50%{transform:scaleX(0.78);opacity:0.10}}
`;
let kfInjected = false;
function injectKF() {
  if (kfInjected || document.getElementById("wng-kf")) { kfInjected=true; return; }
  const s = document.createElement("style");
  s.id = "wng-kf"; s.textContent = KF;
  document.head.appendChild(s);
  kfInjected = true;
}

/* ─── Context messages ────────────────────────────────────────────── */
const CAR_PRAISE = {
  mazda:"Zoom zoom! Nice Mazda 🚗",toyota:"Toyota — bulletproof choice 💪",holden:"Aussie legend! ❤️",
  ford:"Ford tough! On it 🔍",honda:"Honda — reliability king 👑",subaru:"Love that boxer engine 💙",
  nissan:"Nissan — great pick, mate",hyundai:"Hyundai — smart choice 🙌",kia:"Kia — solid engineering 🔧",
  volkswagen:"VW — European class 🇩🇪",bmw:"BMW — very nice 👌",mercedes:"Mercedes — top tier 🌟",
  audi:"Audi — perfection on wheels ✨",mitsubishi:"Mitsubishi — rock solid 💎",
  suzuki:"Suzuki — easy on the wallet 😄",isuzu:"Isuzu — workhorse 🏆",
  jeep:"Jeep — off-road royalty 🏔️",lexus:"Lexus — pure luxury 💎",
};
const IDLE_MSGS = [
  "Need a part? I can help! 🔧","I know every part in the yard 👀",
  "Same-day dispatch before 2pm ⚡","90-day warranty on all parts 🛡️",
  "Tell me make, model & year 🚗",
];
/* ─── Local confetti ──────────────────────────────────────────────── */
function LocalConfetti() {
  const COLS = ["#e31e24","#1e5bb8","#ffd700","#16a34a","#9333ea","#f97316"];
  const ps = useMemo(()=>Array.from({length:28},(_,i)=>({
    id:i,c:COLS[i%COLS.length],
    l:`${6+Math.random()*88}%`,
    dl:`${Math.random()*0.5}s`,dr:`${0.9+Math.random()*0.9}s`,
    sz:4+Math.random()*5,round:Math.random()>0.4,
  })),[]);
  return (
    <div style={{position:"absolute",bottom:"100%",left:"50%",transform:"translateX(-50%)",
      width:200,height:200,pointerEvents:"none",overflow:"visible",zIndex:20}}>
      {ps.map(p=>(
        <div key={p.id} style={{
          position:"absolute",bottom:0,left:p.l,
          width:p.sz,height:p.round?p.sz:p.sz*0.55,
          background:p.c,borderRadius:p.round?"50%":2,
          animation:`wng_conf ${p.dr} ${p.dl} ease-out forwards`,
        }}/>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   WING BOT — Image 1 style: white rounded body, dark visor, blue LED eyes
══════════════════════════════════════════════════════════════════════ */
function WingBot({ state, eyeState }) {
  const excited   = state === "excited";
  const raised    = (state === "excited" || state === "celebrating");
  const dancing   = state === "celebrating";
  const thinking  = state === "thinking";
  const sleeping  = state === "sleeping";

  /* ── Body animation ── */
  const botAnim =
    dancing  ? "wng_wiggle 0.44s ease-in-out infinite" :
    excited  ? "wng_bounce 0.54s cubic-bezier(.36,.07,.19,.97) 3" :
    thinking ? "wng_sway   2.4s  ease-in-out infinite" :
    sleeping ? "wng_sleep  4.2s  ease-in-out infinite" :
               "wng_float  3.6s  ease-in-out infinite";

  /* ── Eye config per state — controls LED eye shape/angle ── */
  // lRot/rRot = rotation of the eye pill (degrees)
  // lH/rH     = height of eye pill in SVG units (thinner = more squinted)
  // eyeC      = LED colour
  const EC = {
    idle:        { lRot:-9, rRot: 3, lH:6.5, rH:6.5, eyeC:"#60a5fa" },
    happy:       { lRot:-13,rRot: 6, lH:6.5, rH:6.5, eyeC:"#60a5fa" },
    excited:     { lRot:-5, rRot:-5, lH:8.5, rH:8.5, eyeC:"#93c5fd" },
    thinking:    { lRot: 6, rRot:-7, lH:5.5, rH:5.5, eyeC:"#60a5fa" },
    sleeping:    { lRot: 0, rRot: 0, lH:2,   rH:2,   eyeC:"#3b82f6" },
    celebrating: { lRot:-16,rRot:16, lH:8.5, rH:8.5, eyeC:"#bfdbfe" },
    signing:     { lRot:-9, rRot: 3, lH:6.5, rH:6.5, eyeC:"#60a5fa" },
  };
  const ec   = EC[state] || EC.idle;
  const blnk = eyeState === "blink" ? 0.06 : 1;

  /* ── Arm pivot (shoulder at ~(20,108) and (100,108)) ── */
  const armLS = {
    transformOrigin:"20px 108px",
    transform: raised ? "rotate(-112deg)" : "rotate(14deg)",
    transition:"transform 0.55s cubic-bezier(0.34,1.56,0.64,1)",
  };
  const armRS = {
    transformOrigin:"100px 108px",
    transform: raised ? "rotate(112deg)" : "rotate(-14deg)",
    transition:"transform 0.55s cubic-bezier(0.34,1.56,0.64,1)",
  };

  /* Helper — eye pill rect (pivot around its centre) */
  const EyePill = ({ cx, cy, rot, h, col }) => (
    <rect
      x={cx - 12} y={cy - h/2}
      width={24} height={h} rx={h/2}
      fill={col}
      transform={`rotate(${rot},${cx},${cy})`}
      style={{ transition:"height 0.15s ease, transform 0.25s ease", filter:"url(#eyeGlow)" }}
    />
  );

  return (
    <div style={{position:"relative",display:"inline-block"}}>
      {dancing && <LocalConfetti/>}

      {/* ── The robot SVG ── */}
      <div style={{animation:botAnim,transformOrigin:"center bottom",display:"inline-block"}}>
        <svg viewBox="0 0 120 145" width={88} height={106} style={{overflow:"visible"}}>
          <defs>
            {/* White body gradient — light blue-white, top-left light source */}
            <radialGradient id="wngBody" cx="36%" cy="26%" r="68%">
              <stop offset="0%"   stopColor="#ffffff"/>
              <stop offset="38%"  stopColor="#eef3ff"/>
              <stop offset="100%" stopColor="#cdd8f5"/>
            </radialGradient>

            {/* Slightly darker gradient for arms */}
            <radialGradient id="wngArm" cx="38%" cy="22%" r="70%">
              <stop offset="0%"   stopColor="#f0f4ff"/>
              <stop offset="100%" stopColor="#c2ceee"/>
            </radialGradient>

            {/* Blue drop shadow — matching Image 1 environment */}
            <filter id="wngShadow" x="-40%" y="-40%" width="180%" height="220%">
              <feDropShadow dx="0" dy="10" stdDeviation="9"  floodColor="#1e40af" floodOpacity="0.28"/>
              <feDropShadow dx="0" dy="3"  stdDeviation="4"  floodColor="#3b82f6" floodOpacity="0.18"/>
            </filter>

            {/* Blue LED glow for eyes */}
            <filter id="eyeGlow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.8" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* Specular highlight shape */}
            <radialGradient id="wngSpec" cx="40%" cy="30%" r="55%">
              <stop offset="0%"   stopColor="rgba(255,255,255,0.75)"/>
              <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
            </radialGradient>

            {/* Red chest pulse gradient */}
            <radialGradient id="wngChest" cx="35%" cy="28%" r="65%">
              <stop offset="0%"   stopColor="#ff6070"/>
              <stop offset="55%"  stopColor="#e31e24"/>
              <stop offset="100%" stopColor="#8a0d10"/>
            </radialGradient>
          </defs>

          {/* ── GROUND SHADOW — stays still while bot floats above ── */}
          <ellipse cx="60" cy="140" rx="38" ry="6"
            fill="rgba(30,64,175,0.18)"
            style={{animation:"wng_shadow 3.6s ease-in-out infinite",transformOrigin:"60px 140px"}}/>

          {/* ── LEFT ARM ── */}
          <ellipse cx="20" cy="108" rx="14" ry="11"
            fill="url(#wngArm)" filter="url(#wngShadow)"
            style={armLS}/>

          {/* ── RIGHT ARM ── */}
          <ellipse cx="100" cy="108" rx="14" ry="11"
            fill="url(#wngArm)" filter="url(#wngShadow)"
            style={armRS}/>

          {/* ── BODY (small rounded blob below head) ── */}
          <ellipse cx="60" cy="108" rx="32" ry="29"
            fill="url(#wngBody)" filter="url(#wngShadow)"/>

          {/* Body specular highlight */}
          <ellipse cx="50" cy="96" rx="14" ry="9"
            fill="url(#wngSpec)" transform="rotate(-12,50,96)"/>

          {/* Subtle blue chest indicator */}
          <circle cx="60" cy="114" r="5" fill="#60a5fa" opacity="0.7"
            style={{animation:"wng_pulse 2.2s ease-in-out infinite",filter:"url(#eyeGlow)"}}/>

          {/* ── HEAD (large rounded rect — like a TV screen case) ── */}
          <rect x="12" y="8" width="96" height="72" rx="24"
            fill="url(#wngBody)" filter="url(#wngShadow)"/>

          {/* Head specular highlight */}
          <ellipse cx="44" cy="24" rx="24" ry="11"
            fill="url(#wngSpec)" transform="rotate(-10,44,24)"/>

          {/* ── VISOR (dark screen on head) ── */}
          <rect x="22" y="20" width="76" height="40" rx="13"
            fill="#14172a"/>
          {/* Visor inner gloss */}
          <rect x="23" y="21" width="74" height="16" rx="10"
            fill="rgba(255,255,255,0.04)"/>

          {/* ── EYES — blue LED pills inside visor ── */}
          <g style={{transform:`scaleY(${blnk})`,transformOrigin:"60px 40px",transition:"transform 0.07s ease"}}>
            <EyePill cx={40} cy={40} rot={ec.lRot} h={ec.lH} col={ec.eyeC}/>
            <EyePill cx={80} cy={40} rot={ec.rRot} h={ec.rH} col={ec.eyeC}/>
          </g>

          {/* Sleeping eyes override — thin horizontal line */}
          {sleeping && (
            <>
              <rect x="29" y="38.5" width="22" height="3" rx="1.5" fill={ec.eyeC}/>
              <rect x="69" y="38.5" width="22" height="3" rx="1.5" fill={ec.eyeC}
                style={{filter:"url(#eyeGlow)"}}/>
            </>
          )}

          {/* ── ANTENNA (small subtle top of head) ── */}
          <rect x="58" y="2" width="4" height="10" rx="2" fill="#c8d4ee"/>
          <circle cx="60" cy="1" r="4.5" fill="#60a5fa"
            style={{animation:"wng_antpls 1.4s ease-in-out infinite",filter:"url(#eyeGlow)"}}/>
          <circle cx="60" cy="1" r="2"  fill="rgba(219,234,254,0.9)"/>

          {/* ── SLEEPING ZZZ ── */}
          {sleeping && [0,1,2].map(i=>(
            <text key={i} x={95+i*9} y={22-i*10}
              fontSize={9+i*4} fill="#94a3b8" fontWeight="bold"
              style={{animation:`wng_zzz ${1.2+i*0.4}s ${i*0.38}s ease-out infinite`}}>
              z
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

/* ─── Speech bubble ───────────────────────────────────────────────── */
function WingBubble({ message, visible }) {
  if (!message) return null;
  return (
    <div style={{
      marginBottom:8,maxWidth:"min(220px, calc(100vw - 108px))",alignSelf:"flex-end",
      opacity:visible?1:0,
      transform:visible?"translateY(0) scale(1)":"translateY(8px) scale(0.9)",
      transition:"all 0.38s cubic-bezier(0.34,1.56,0.64,1)",
      pointerEvents:"none",
    }}>
      <div style={{
        background:"#fff",color:"#0f172a",
        padding:"9px 14px",
        borderRadius:18,borderBottomRightRadius:5,
        fontSize:13,fontWeight:600,lineHeight:1.4,
        boxShadow:"0 4px 18px rgba(30,64,175,0.14), 0 1px 4px rgba(0,0,0,0.07)",
        border:"1px solid #e8f0ff",
        whiteSpace:"normal",
        wordBreak:"break-word",
      }}>
        {message}
      </div>
      <div style={{
        width:0,height:0,marginRight:18,marginLeft:"auto",
        borderLeft:"7px solid transparent",borderRight:"7px solid transparent",
        borderTop:"8px solid #fff",
        filter:"drop-shadow(0 1px 0 #e8f0ff)",
      }}/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ChatLauncher — state machine, ONE info at a time, no walking
═══════════════════════════════════════════════════════════════════ */
function ChatLauncher({ onClick }) {
  const location      = useLocation();
  const { items }     = useCart();

  const [state,    setState]   = useState("idle");
  const [eyeState, setEye]     = useState("normal");
  const [bubble,   setBubble]  = useState(null);
  const [showBub,  setShowBub] = useState(false);

  const stateRef  = useRef("idle");
  const showBubRef= useRef(false);
  const prevItems = useRef(items.length);
  const prevPath  = useRef(location.pathname);
  const cleanupRef= useRef([]);

  useEffect(()=>{ stateRef.current = state; },[state]);
  useEffect(()=>{ showBubRef.current = showBub; },[showBub]);
  useEffect(()=>{ injectKF(); },[]);

  const clearTimers = () => { cleanupRef.current.forEach(clearTimeout); cleanupRef.current=[]; };

  /* ── Show message bubble ── */
  const showMsg = useCallback((msg, ns="happy", dur=4500)=>{
    clearTimers();
    setBubble(msg); setShowBub(true); showBubRef.current=true;
    setState(ns); stateRef.current=ns;
    const t1=setTimeout(()=>{ setShowBub(false); showBubRef.current=false; }, dur);
    const t2=setTimeout(()=>{ setState("idle"); }, dur+600);
    cleanupRef.current=[t1,t2];
  },[]);

  /* ── Eye blink ── */
  useEffect(()=>{
    let t;
    const blink=()=>{ setEye("blink"); setTimeout(()=>setEye("normal"),120); t=setTimeout(blink,2800+Math.random()*3800); };
    t=setTimeout(blink,2200);
    return()=>clearTimeout(t);
  },[]);

  /* ── Random eye glance ── */
  useEffect(()=>{
    const dirs=["normal","normal","normal","left","right","up"];
    const t=setInterval(()=>{
      if(["idle","thinking"].includes(stateRef.current)){
        const d=dirs[Math.floor(Math.random()*dirs.length)];
        setEye(d); setTimeout(()=>setEye("normal"),950);
      }
    },5800);
    return()=>clearInterval(t);
  },[]);

  /* ── Initial greeting ── */
  useEffect(()=>{
    const t=setTimeout(()=>showMsg("G'day! I'm Wing 👋 Need a part?","happy",4800), 3000);
    return()=>clearTimeout(t);
  },[]); // eslint-disable-line

  /* ── Cart detection ── */
  useEffect(()=>{
    if(items.length>prevItems.current){
      const ms=["Nice pick! Added to cart! 🛒","Great choice! 💪","Added! One step closer ✨"];
      showMsg(ms[Math.floor(Math.random()*ms.length)],"excited",4000);
    }
    prevItems.current=items.length;
  },[items.length,showMsg]);

  /* ── Page context ── */
  useEffect(()=>{
    const path=location.pathname;
    if(path===prevPath.current) return;
    prevPath.current=path;
    if(path==="/"||path==="/home") setTimeout(()=>showMsg("G'day! Looking for a car part? 🚗","happy",4500),600);
    else if(path.startsWith("/shop")){
      const q=(new URLSearchParams(location.search).get("q")||"").toLowerCase();
      const make=Object.keys(CAR_PRAISE).find(k=>q.includes(k));
      if(make) setTimeout(()=>showMsg(CAR_PRAISE[make],"excited",4500),500);
    } else if(path.startsWith("/product/")) setTimeout(()=>showMsg("Want me to check fitment? 💬","thinking",5000),1200);
    else if(path.includes("checkout")) setTimeout(()=>showMsg("Almost there! 🎉","celebrating",5500),400);
    else if(path.includes("confirmed")) setTimeout(()=>showMsg("Order placed! Legend! 🏆","celebrating",6000),300);
  },[location,showMsg]);

  /* ── Idle ── */
  useEffect(()=>{
    let count=0;
    const reset=()=>{ count=0; if(stateRef.current==="sleeping") setState("idle"); };
    const tick=setInterval(()=>{
      if(stateRef.current==="idle"){ count++;
        if(count===3) showMsg(IDLE_MSGS[Math.floor(Math.random()*IDLE_MSGS.length)],"thinking",5000);
        if(count>=7)  setState("sleeping");
      } else count=0;
    },20000);
    window.addEventListener("mousemove",reset,{passive:true});
    window.addEventListener("keydown",  reset,{passive:true});
    window.addEventListener("scroll",   reset,{passive:true});
    return()=>{ clearInterval(tick); window.removeEventListener("mousemove",reset); window.removeEventListener("keydown",reset); window.removeEventListener("scroll",reset); };
  },[showMsg]);

  return (
    <div style={{position:"fixed",bottom:14,right:24,zIndex:40,
      display:"flex",flexDirection:"column",alignItems:"flex-end",
      maxWidth:"calc(100vw - 32px)"}}
      data-testid="chat-widget-toggle-wrap">

      <WingBubble message={bubble} visible={showBub}/>

      {/* Online dot outside robot */}
      <div style={{position:"relative",display:"inline-block",cursor:"pointer"}}
        onClick={()=>{ setState("excited"); onClick(); }}
        data-testid="chat-widget-toggle" aria-label="Open chat with Wing">
        <div style={{
          position:"absolute",top:14,right:2,zIndex:10,
          width:10,height:10,borderRadius:"50%",
          background:"#4ade80",border:"2px solid #fff",
          boxShadow:"0 0 0 3px rgba(74,222,128,0.3)",
        }}/>
        <WingBot state={state} eyeState={eyeState}/>
      </div>

      {/* Permanent label — always visible, doesn't compete with the speech bubble */}
      <div style={{
        marginTop:-4,
        background:"#0b1a3b",color:"#fff",
        padding:"4px 12px",borderRadius:20,
        fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
        boxShadow:"0 2px 8px rgba(11,26,59,0.25)",
        display:"flex",alignItems:"center",gap:5,
        cursor:"pointer",
      }} onClick={()=>{ setState("excited"); onClick(); }}>
        <span style={{width:6,height:6,borderRadius:"50%",background:"#4ade80",flexShrink:0}}/>
        Live Chat
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ChatWidget — all chat API logic 100% unchanged
═══════════════════════════════════════════════════════════════════ */
export default function ChatWidget() {
  const { user } = useCustomerAuth();
  const [open,           setOpen]          = useState(false);
  const [started,        setStarted]       = useState(false);
  const [conversationId, setConversationId]= useState(null);
  const [name,  setName]  = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [text,  setText]  = useState("");
  const [messages,    setMessages]    = useState([]);
  const [sending,     setSending]     = useState(false);
  const [attachments, setAttachments] = useState([]);
  const scrollRef    = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(()=>{
    try{ const s=JSON.parse(localStorage.getItem(STORAGE_KEY)||"{}");
      if(s.conversation_id){ setConversationId(s.conversation_id); setName(s.name||""); setEmail(s.email||""); setPhone(s.phone||""); setStarted(true); }
    }catch{}
  },[]);
  useEffect(()=>{ if(user){ setName(n=>n||user.name||""); setEmail(e=>e||user.email||""); } },[user]);
  useEffect(()=>{
    if(!open||!conversationId) return;
    const load=async()=>{ try{ const{data}=await api.get(`/messages/${conversationId}`); setMessages(data||[]); }catch{} };
    load(); const t=setInterval(load,8000); return()=>clearInterval(t);
  },[open,conversationId]);
  useEffect(()=>{ if(scrollRef.current) scrollRef.current.scrollTop=scrollRef.current.scrollHeight; },[messages,open]);

  const send=async()=>{
    if(!text.trim()&&attachments.length===0) return;
    if(!started&&(!name.trim()||!email.trim())) return;
    setSending(true);
    try{
      const{data}=await api.post("/messages",{name,email,phone,subject:started?"Chat follow-up":"New chat",body:text,
        attachments:attachments.map(a=>({kind:a.kind,name:a.name,data_url:a.data_url})),conversation_id:conversationId});
      const cid=data.conversation_id;
      setConversationId(cid); setStarted(true); setText(""); setAttachments([]);
      localStorage.setItem(STORAGE_KEY,JSON.stringify({conversation_id:cid,name,email,phone}));
      setMessages(m=>[...m,data]);
    }catch{} finally{ setSending(false); }
  };
  const onFile=e=>{
    Array.from(e.target.files||[]).forEach(file=>{
      if(attachments.length>=4) return;
      if(file.size>MAX_FILE_BYTES){ alert(`${file.name} too large`); return; }
      const isImg=file.type.startsWith("image/"),isPdf=file.type==="application/pdf";
      if(!isImg&&!isPdf){ alert("Images or PDFs only"); return; }
      const r=new FileReader();
      r.onload=ev=>setAttachments(arr=>[...arr,{kind:isImg?"image":"pdf",name:file.name,data_url:ev.target.result}]);
      r.readAsDataURL(file);
    });
    if(fileInputRef.current) fileInputRef.current.value="";
  };
  const removeAttachment=idx=>setAttachments(arr=>arr.filter((_,i)=>i!==idx));

  return (
    <>
      {!open&&<ChatLauncher onClick={()=>setOpen(true)}/>}
      {open&&(
        <div className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{border:"1px solid #dde3ef"}} data-testid="chat-widget-panel">
          <div className="px-4 py-3 flex items-center justify-between"
            style={{background:"linear-gradient(135deg,#0b1a3b 0%,#1a3168 100%)"}}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-[12px] shrink-0"
                style={{background:"#1e3a7a",border:"1px solid rgba(255,255,255,0.15)"}}>W</div>
              <div>
                <div className="font-heading font-bold text-sm uppercase tracking-wide text-white">Wing — BCP Support</div>
                <div className="text-[10px] font-mono text-blue-300/60 uppercase tracking-widest">Mon–Fri · usually fast</div>
              </div>
            </div>
            <button onClick={()=>setOpen(false)} className="text-white/60 hover:text-white" aria-label="Close" data-testid="chat-widget-close"><X className="h-5 w-5"/></button>
          </div>
          {!started&&(
            <div className="p-4 space-y-2 border-b" style={{background:"#f5f7fb",borderColor:"#eef2f8"}}>
              <p className="text-[12px]" style={{color:"#64748b"}}>Quick details so Wing can help:</p>
              {[[name,setName,"Your name *","chat-name","text"],[email,setEmail,"Email *","chat-email","email"],[phone,setPhone,"Phone (optional)","","tel"]].map(([v,sv,ph,tid,tp])=>(
                <input key={ph} value={v} onChange={e=>sv(e.target.value)} placeholder={ph} type={tp}
                  className="w-full h-10 px-3 rounded-xl text-sm outline-none"
                  style={{border:"1px solid #dde3ef",color:"#0f172a",background:"#fff"}}
                  data-testid={tid||undefined}/>
              ))}
            </div>
          )}
          <div ref={scrollRef} className="flex-1 max-h-[440px] min-h-[220px] overflow-y-auto px-4 py-3 space-y-3"
            style={{background:"#f9fafb"}} data-testid="chat-messages">
            {messages.length===0&&(<div className="text-center text-xs py-6 space-y-1.5" style={{color:"#94a3b8"}}><div className="text-2xl">🔧</div><div>Tell Wing which part you need.</div><div>Make, model &amp; year = best results!</div></div>)}
            {messages.map(m=><MessageBubble key={m.id} m={m}/>)}
          </div>
          {attachments.length>0&&(
            <div className="px-3 py-2 flex gap-2 flex-wrap" style={{borderTop:"1px solid #eef2f8",background:"#fff"}} data-testid="chat-attachments-preview">
              {attachments.map((a,i)=>(
                <div key={i} className="relative w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center" style={{border:"1px solid #dde3ef",background:"#f5f7fb"}}>
                  {a.kind==="image"?<img src={a.data_url} alt="" className="w-full h-full object-cover"/>:<FileText className="h-5 w-5" style={{color:"#e31e24"}}/>}
                  <button type="button" onClick={()=>removeAttachment(i)} className="absolute top-0 right-0 w-4 h-4 flex items-center justify-center text-white rounded-bl-sm" style={{background:"rgba(0,0,0,0.7)"}}><X className="h-2.5 w-2.5"/></button>
                </div>
              ))}
            </div>
          )}
          <div className="p-3 flex gap-2 items-center" style={{borderTop:"1px solid #eef2f8"}}>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple onChange={onFile} className="hidden" data-testid="chat-file-input"/>
            <button onClick={()=>fileInputRef.current?.click()} className="h-10 w-10 rounded-xl flex items-center justify-center" style={{border:"1px solid #dde3ef",color:"#64748b",background:"#f5f7fb"}} data-testid="chat-attach-btn"><Paperclip className="h-4 w-4"/></button>
            <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder="Ask Wing about any part…" className="flex-1 h-10 px-3 rounded-xl text-sm outline-none"
              style={{border:"1px solid #dde3ef",color:"#0f172a",background:"#fff"}} data-testid="chat-input"/>
            <button onClick={send} disabled={sending||(!text.trim()&&attachments.length===0)||(!started&&(!name||!email))}
              className="h-10 w-10 rounded-xl flex items-center justify-center text-white disabled:opacity-40"
              style={{background:"linear-gradient(135deg,#e31e24,#b71820)"}} data-testid="chat-send">
              <Send className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({m}){
  if(m.sender==="system") return(<div className="flex justify-center"><div className="text-[11px] px-3 py-2 rounded-xl max-w-[85%] text-center" style={{background:"#fef3c7",border:"1px solid #fde68a",color:"#92400e"}}>{m.body}</div></div>);
  const iC=m.sender==="customer";
  return(
    <div className={`flex ${iC?"justify-end":"justify-start"}`}>
      <div className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm ${iC?"rounded-br-sm":"rounded-bl-sm shadow-sm"}`}
        style={iC?{background:"#1e5bb8",color:"#fff"}:{background:"#fff",color:"#0f172a",border:"1px solid #eef2f8"}}>
        {!iC&&<div className="text-[10px] font-mono uppercase tracking-widest text-[#e31e24] mb-0.5">BCP Support</div>}
        {m.body&&<div className="whitespace-pre-wrap break-words">{m.body}</div>}
        {m.attachments?.length>0&&(<div className="mt-2 grid grid-cols-2 gap-1.5">{m.attachments.map((a,i)=>a.kind==="image"?(<button key={i} type="button" onClick={()=>{const w=window.open("","_blank");const img=`<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${a.data_url}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body></html>`;w.document.write(img);w.document.close();}} className="block aspect-square rounded-lg overflow-hidden cursor-zoom-in"><img src={a.data_url} alt={a.name} className="w-full h-full object-cover"/></button>):(<a key={i} href={a.data_url} download={a.name} className={`flex items-center gap-2 px-2 py-2 rounded-lg border ${iC?"border-white/30":"border-zinc-200"}`}><FileText className={`h-4 w-4 ${iC?"text-white":"text-[#e31e24]"}`}/><div className="text-[11px] truncate">{a.name}</div></a>))}</div>)}
        {m.payment_link?.url&&(<a href={m.payment_link.url} target="_blank" rel="noreferrer" className={`mt-2 block rounded-xl overflow-hidden border ${iC?"border-white/30":"border-[#1e5bb8]"}`}><div className={`px-3 py-2 ${iC?"bg-white/10":"bg-[#1e5bb8]"} text-white`}><div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Stripe payment</div><div className="font-heading text-base font-bold">{formatAud(m.payment_link.amount)}</div><div className="text-[11px] opacity-80">Order {m.payment_link.order_number}</div></div><div className={`px-3 py-2 text-[11px] flex items-center justify-between ${iC?"bg-white/5 text-white":"bg-zinc-50 text-[#0b1a3b]"}`}>Pay securely<ExternalLink className="h-3 w-3"/></div></a>)}
        {m.product_link&&(<a href={`/product/${m.product_link.id}`} className={`mt-2 flex gap-2 rounded-xl overflow-hidden border ${iC?"border-white/30 bg-white/5":"border-[#eef2f8] bg-[#f5f7fb]"}`}><div className="w-12 h-12 bg-zinc-100 shrink-0">{m.product_link.image_url&&<img src={m.product_link.image_url} alt="" className="w-full h-full object-cover"/>}</div><div className="py-1 pr-2 min-w-0"><div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{m.product_link.sku}</div><div className="text-xs font-bold truncate">{m.product_link.name}</div>{m.product_link.price&&<div className="text-xs font-mono">{formatAud(m.product_link.price)}</div>}</div></a>)}
        <div className={`text-[10px] mt-1 ${iC?"text-blue-200":"text-[#94a3b8]"}`}>{new Date(m.created_at).toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"})}</div>
      </div>
    </div>
  );
}